import { useRef, useEffect, useState } from 'react';

// Declaration for window.VANTA and window.THREE
declare global {
  interface Window {
    VANTA: any;
    THREE: any;
  }
}

/** Selectable Vanta effects. 'dots' and 'fog' were removed from Settings
 *  (kept in the union so a legacy stored value would still render if ever
 *  re-enabled; nothing routes to them today). */
export type VantaEffect = 'waves' | 'dots' | 'fog';

interface VantaBackgroundProps {
  effect: VantaEffect;
  className?: string;
  opacity?: number;
  onReady?: () => void;
}

const SCRIPTS = {
  three: 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r134/three.min.js',
  waves: 'https://cdn.jsdelivr.net/npm/vanta@latest/dist/vanta.waves.min.js',
  dots: 'https://cdn.jsdelivr.net/npm/vanta@latest/dist/vanta.dots.min.js',
  fog: 'https://cdn.jsdelivr.net/npm/vanta@latest/dist/vanta.fog.min.js',
};

// Helper to load script
const loadScript = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.body.appendChild(script);
  });
};

const VantaBackground: React.FC<VantaBackgroundProps> = ({ effect, className = '', opacity, onReady }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [vantaEffect, setVantaEffect] = useState<any>(null);
  const [scriptsLoaded, setScriptsLoaded] = useState(false);
  const onReadyCalledRef = useRef(false);

  // Load scripts
  useEffect(() => {
    const loadDependencies = async () => {
      try {
        // Load Three.js first
        if (!window.THREE) {
          await loadScript(SCRIPTS.three);
        }

        // Load specific Vanta effect script
        const effectScript = SCRIPTS[effect as keyof typeof SCRIPTS];
        if (effectScript) {
          await loadScript(effectScript);
          setScriptsLoaded(true);
        }
      } catch (error) {
        console.error('Failed to load Vanta scripts:', error);
      }
    };

    loadDependencies();
  }, [effect]);

  // Initialize Vanta effect
  useEffect(() => {
    if (!scriptsLoaded || !containerRef.current || !window.VANTA || !window.THREE) return;

    if (vantaEffect) {
      vantaEffect.destroy();
    }

    const initVanta = () => {
      try {
        // Double-check THREE.js is available
        if (!window.THREE) {
          console.error('THREE.js not loaded');
          return;
        }

        let instance;
        const commonOptions = {
          el: containerRef.current,
          mouseControls: true,
          touchControls: true,
          gyroControls: false,
          minHeight: 200.00,
          minWidth: 200.00,
          scale: 1.00,
          scaleMobile: 1.00,
          THREE: window.THREE // Explicitly pass THREE.js reference
        };

        switch (effect) {
          case 'waves':
            if (window.VANTA.WAVES) {
              instance = window.VANTA.WAVES({
                ...commonOptions,
                color: 0x050505, // Dark base
                shininess: 16.00,
                waveHeight: 11.50,
                waveSpeed: 0.65,
                zoom: 0.89
              });
            }
            break;
          case 'dots':
            if (window.VANTA.DOTS) {
              instance = window.VANTA.DOTS({
                ...commonOptions,
                color: 0x181818, // Dark Grey
                color2: 0x000000,
                backgroundColor: 0x000000, // Black
                size: 3.60,
                spacing: 17.00,
                showLines: false
              });
            }
            break;
          case 'fog':
            if (window.VANTA.FOG) {
              instance = window.VANTA.FOG({
                ...commonOptions,
                highlightColor: 0x090909, // Dark Grey/Black
                midtoneColor: 0x000000,   // Black
                lowlightColor: 0x671abe,  // Deep Purple
                baseColor: 0x161415,      // Dark background
                blurFactor: 0.67,
                speed: 2.50
              });
            }
            break;
          default:
            break;
        }

        if (instance) {
          setVantaEffect(instance);
          // Signal that the effect is ready
          if (onReady && !onReadyCalledRef.current) {
            onReadyCalledRef.current = true;
            onReady();
          }
        }
      } catch (error) {
        console.error('Error initializing Vanta effect:', error);
      }
    };

    // Small delay to ensure scripts are parsed
    setTimeout(initVanta, 100);

    return () => {
      if (vantaEffect) vantaEffect.destroy();
    };
  }, [scriptsLoaded, effect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (vantaEffect) vantaEffect.destroy();
    };
  }, [vantaEffect]);

  // Opacity settings for each effect
  const defaultOpacity = {
    waves: 0.4,
    dots: 0.7,
    fog: 0.7,
    net: 0.7,
    rings: 0.7,
    cells: 0.7
  }[effect] || 1;

  const finalOpacity = opacity !== undefined ? opacity : defaultOpacity;

  return (
    <div
      ref={containerRef}
      className={`w-full h-full absolute inset-0 -z-10 ${className}`}
      style={{
        background: '#050505', // Fallback
        opacity: finalOpacity
      }}
    />
  );
};

export default VantaBackground;
